/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, Icon, PagedTable, Text } from 'bold-ui'
import useSession from 'components/auth/useSession'
import { DateTime } from 'components/date'
import { usePecField } from 'components/form/final-form/useField'
import { TableBox, usePagedTableProps } from 'components/table'
import { useEncaminhamentosExternosQuery } from 'graphql/hooks.generated'
import {
  ClassificacaoRiscoEncaminhamentoEnum,
  EncaminhamentoExternoQueryInput,
  EncaminhamentosExternosQuery,
} from 'graphql/types.generated'
import { useState } from 'react'
import { MetaPath } from 'util/metaPath'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'

import { EncaminhamentoExternoModel, EncaminhamentoExternoPlanoModel } from '../model'
import { mergeEncaminhamentos } from '../util/mergeEncaminhamentos'
import { EncaminhamentoExternoTableButtons } from './EncaminhamentoExternoTableButtons'
import { EncaminhamentoExternoFilter } from './EncaminhamentoExternoTableHeader'
import { StatusClassificacaoRiscoEncaminhamento } from './StatusClassificacaoRiscoEncaminhamento'

export type EncaminhamentosModel = EncaminhamentosExternosQuery['encaminhamentos']
export type EncaminhamentoExternoTableRowModel = EncaminhamentosExternosQuery['encaminhamentos']['content'][0]

export interface EncaminhamentoExternoTableProps {
  prontuarioId: ID
  name: MetaPath<EncaminhamentoExternoPlanoModel>
  atendimentoProfissional: AtendimentoProfissional
  remove(index: number): void
}

export interface EncaminhamentoExternoRowModel {
  id: ID
  classificacaoRiscoEncaminhamento: ClassificacaoRiscoEncaminhamentoEnum
  atendimentoProfissional: {
    id: ID
    iniciadoEm: any
    lotacao: {
      id: ID
      profissional: {
        id: ID
        nome: string
      }
    }
  }
  especialidadeSisreg: {
    id: ID
    descricao: string
  }
  hipoteseDiagnostica: {
    id: ID
    nome: string
  }
  isRegistradoAgora: boolean
}

export default function EncaminhamentoExternoTable(props: EncaminhamentoExternoTableProps) {
  const { prontuarioId, name, atendimentoProfissional, remove } = props
  const { input } = usePecField({ name })

  const plano = input.value as EncaminhamentoExternoPlanoModel

  const [tableState, setTableState] = useState<EncaminhamentoExternoQueryInput>({
    prontuarioId,
    pageParams: { page: 0, size: 5 },
  })

  const {
    data: { profissional },
  } = useSession()

  const {
    data: { encaminhamentos },
  } = useEncaminhamentosExternosQuery({
    variables: {
      input: {
        prontuarioId,
        query: tableState.query,
        somenteMeusEncaminhamentosExternos: tableState.somenteMeusEncaminhamentosExternos,
        especialidadeId: tableState.especialidadeId,
        periodo: tableState.periodo,
      },
    },
  })

  const renderClassificacaoRisco = (row: EncaminhamentoExternoRowModel) => (
    <StatusClassificacaoRiscoEncaminhamento status={row?.classificacaoRiscoEncaminhamento} />
  )

  const renderData = (row: EncaminhamentoExternoRowModel) => (
    <DateTime value={row?.atendimentoProfissional?.iniciadoEm} format='DD/MM/YYYY' />
  )

  const renderEspecialidade = (row: EncaminhamentoExternoRowModel) => <Text>{row?.especialidadeSisreg?.descricao}</Text>

  const renderHipoteseDiagnostica = (row: EncaminhamentoExternoRowModel) => (
    <Text>{row?.hipoteseDiagnostica?.nome}</Text>
  )

  const renderStatus = (row: EncaminhamentoExternoRowModel) => {
    return (
      row.isRegistradoAgora && (
        <Text color='primary'>
          <HFlow alignItems='center' hSpacing={0.5}>
            <Icon size={1} icon='clockOutline' />
            Registrado agora
          </HFlow>
        </Text>
      )
    )
  }

  const renderButtons = (row: EncaminhamentoExternoRowModel) => (
    <EncaminhamentoExternoTableButtons encaminhamento={row} remove={remove} />
  )

  const encaminhamentosAntigos = encaminhamentos?.content ?? []
  const encaminhamentosRecentes = plano.encaminhamentosRecentes as EncaminhamentoExternoModel[]

  const encaminhamentosExibir: EncaminhamentoExternoRowModel[] = mergeEncaminhamentos(
    encaminhamentosAntigos,
    encaminhamentosRecentes,
    atendimentoProfissional,
    profissional.id,
    tableState
  )

  const { page: currentPage, size: itensPerPage } = tableState.pageParams
  const totalElements = encaminhamentosExibir?.length ?? 0
  const totalPages = Math.ceil(totalElements / itensPerPage)
  const content = encaminhamentosExibir?.slice(currentPage * itensPerPage, (currentPage + 1) * itensPerPage) ?? []
  const encaminhamentosPage = {
    content: content,
    pageInfo: {
      number: currentPage,
      size: itensPerPage,
      totalPages: totalPages,
      totalElements: totalElements,
      first: currentPage === 0,
      last: currentPage === totalPages - 1,
      numberOfElements: content.length,
    },
    paginationOptions: [5, 10, 30],
  }

  const tableProps = usePagedTableProps({
    loading: false,
    onChange: setTableState,
    result: encaminhamentosPage,
  })

  return (
    <TableBox header={<EncaminhamentoExternoFilter onChange={setTableState} filter={tableState} />}>
      <PagedTable<EncaminhamentoExternoRowModel>
        {...tableProps}
        columns={[
          { name: 'classificacaoRisco', render: renderClassificacaoRisco, style: classes.classificacaoColumn },
          { name: 'data', header: 'Data', render: renderData, style: classes.dataColumn },
          {
            name: 'especialidade',
            header: 'Especialidade',
            render: renderEspecialidade,
            style: classes.especialidadeColumn,
          },
          {
            name: 'hipoteseDiagnostica',
            header: 'Hipótese diagnóstica',
            render: renderHipoteseDiagnostica,
            style: classes.hipoteseColumn,
          },
          { name: 'status', render: renderStatus, style: classes.adicionadoAgoraColumn },
          { name: 'buttons', render: renderButtons, style: classes.buttonsColumn },
        ]}
      />
    </TableBox>
  )
}

const classes = {
  classificacaoColumn: css`
    width: 4%;
  `,
  dataColumn: css`
    width: 10%;
    padding-left: 0;
  `,
  especialidadeColumn: css`
    width: 21%;
  `,
  hipoteseColumn: css`
    width: 30%;
  `,
  adicionadoAgoraColumn: css`
    width: 15%;
    grid-gap: 0.58rem;
  `,
  buttonsColumn: css`
    width: 20%;
  `,
}
